import { border2, spacing, TmElement } from '@thiememeulenhoff/lit-sdk';
import { customElement, property } from 'lit/decorators.js';
import { css, html } from 'lit';
import { tagName } from '../../helpers';
import { TabComponent } from './sub/tab.component';
import { PanelComponent } from './sub/panel.component';

const tag = tagName('tabs');

@customElement(tag)
export class TabsComponent extends TmElement {
  @property({ type: String, reflect: true })
  variant: 'default' | 'filter' | 'header' = 'default';

  static override styles = [
    css`
      :where(nav) {
        display: flex;
        flex-direction: row;
      }

      :host([variant='default']) {
        :where(nav) {
          border-block-end: ${border2(1, 'border-strong')};
          gap: ${spacing(400)};
        }
      }

      :host([variant='filter']) {
        display: block;

        :where(nav) {
          column-gap: ${spacing(300)};
          flex-wrap: wrap;
          padding-inline-start: ${spacing(25)};
        }
      }

      :host([variant='header']) {
        display: block;

        :where(nav) {
          flex-wrap: nowrap;
        }

        ::slotted(a-tab) {
          align-items: center;
          border-inline-start: ${border2(1, 'border-subtle', 'solid')};
          display: flex;
          padding-inline: ${spacing(100)};
        }

        ::slotted(a-tab:first-child) {
          border-inline-start: 0 none;
        }
      }
    `,
  ];

  private tabKeydownHandler = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      (event.target as HTMLElement).click();
    }
  };

  override render() {
    return html`<nav role="tablist" part="nav">
        <slot name="tab" @slotchange=${this.registerSlotSelectedEvents}></slot>
      </nav>
      <slot @slotchange=${this.registerPanelsToSlots}></slot>`;
  }

  override firstUpdated() {
    this.#setFirstSelectedTab();
    this.#keyboardNavigation();
    this.registerPanelsToSlots();
    this.registerSlotSelectedEvents();
  }

  override disconnectedCallback() {
    this.#removeKeyboardNavigation();
    super.disconnectedCallback();
  }

  #keyboardNavigation() {
    const tabs = this.#getSlottedTabs();
    tabs.forEach((tab) => {
      tab.addEventListener('keydown', this.tabKeydownHandler);
    });
  }

  #removeKeyboardNavigation() {
    const tabs = this.#getSlottedTabs();
    tabs.forEach((tab) => {
      tab.removeEventListener('keydown', this.tabKeydownHandler);
    });
  }

  #setFirstSelectedTab = () => {
    const tabs = this.#getSlottedTabs();
    const selectedTabs = tabs.filter((tab) => tab.selected);

    if (tabs.length === 0) {
      return;
    }

    if (selectedTabs.length === 0) {
      tabs[0].setSelected(true);
    } else if (selectedTabs.length > 1) {
      selectedTabs.forEach((tab) => {
        tab.setSelected(false);
      });
      selectedTabs[0].setSelected(true);
    }
  };

  #getSlottedTabs = () => {
    const assignedSlots: HTMLSlotElement | null | undefined =
      this.shadowRoot?.querySelector('slot[name="tab"]');
    const flatAssignedSlots = assignedSlots?.assignedElements({
      flatten: true,
    });

    if (!flatAssignedSlots) {
      return [];
    }

    return (flatAssignedSlots.filter((slot) => slot instanceof TabComponent) ??
      []) as Array<TabComponent>;
  };

  #getSlottedPanels = () => {
    const unassignedSlots: HTMLSlotElement | null | undefined =
      this.shadowRoot?.querySelector('slot:not([name])');
    const flatUnassignedSlots = unassignedSlots?.assignedElements({
      flatten: true,
    });

    if (!flatUnassignedSlots) {
      return [];
    }

    return (flatUnassignedSlots.filter(
      (slot) => slot instanceof PanelComponent,
    ) ?? []) as Array<PanelComponent>;
  };

  private registerSlotSelectedEvents = () => {
    const tabs = this.#getSlottedTabs();

    tabs.forEach((tab) => {
      const onSelected = () => {
        tabs.filter((t) => t !== tab).forEach((t) => t.setSelected(false));

        this.dispatchLocalEvent({
          eventName: 'tab-selected',
          detail: tab.for,
          bubbles: false,
        });
      };

      tab.addEventListener('selected', onSelected);
    });
  };

  private registerPanelsToSlots = () => {
    const tabs = this.#getSlottedTabs();
    const panels = this.#getSlottedPanels();

    tabs.forEach((tab) => {
      panels
        .filter((panel) => panel.name === tab.for)
        .forEach((panel) => {
          tab.addPanel(panel);
        });
    });
  };

  openTab(tabName: string, switchFocusTo = true) {
    const tabs = this.#getSlottedTabs();
    const tab = tabs.find((tab) => tab.for === tabName);

    if (!tab || tab.selected) {
      return;
    }

    tab.setSelected(true);
    if (switchFocusTo) tab.focus();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-tabs': TabsComponent;
  }
}
